import { Pencil, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { glyphOf, useSiteTypeLabel } from '../types';
import { AddressLink, PhoneLink } from './AddressLink';
import type { Site, Supplier } from './api';
import { Card } from './Card';

/** The registered office first, then the other sites as the API orders them (active first). */
export function sitesOf(supplier: Supplier): readonly Site[] {
  const office = supplier.registeredOffice;
  const others = supplier.sites.filter((site) => site.id !== office?.id);
  return office ? [office, ...others] : others;
}

/**
 * «Sedi» (board 3.1): each site with its sign, type, address (to the maps app, copyable),
 * phone and uses. «Aggiungi una sede» for anyone the API lets (`can.addSite`), «Modifica» on each
 * site for whoever may edit the registry (`can.edit`); without either the card is read only, and
 * hidden when there is no site.
 */
export function SitesCard({
  supplier,
  onAdd,
  onEdit,
}: {
  readonly supplier: Supplier;
  /** Absent: no «Aggiungi una sede» (a merged supplier, or `can.addSite` false). */
  readonly onAdd?: () => void;
  /** Absent: no «Modifica» on the sites. */
  readonly onEdit?: (site: Site) => void;
}) {
  const { t } = useTranslation('suppliers');
  const sites = sitesOf(supplier);
  if (sites.length === 0 && onAdd === undefined) return null;
  return (
    <Card
      title={t('sites.title')}
      hint={String(sites.length)}
      action={
        onAdd ? (
          <Button size="sm" variant="ghost" onClick={onAdd}>
            <Plus aria-hidden="true" />
            {t('sites.add')}
          </Button>
        ) : null
      }
    >
      {sites.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('sites.none')}</p>
      ) : (
        <ul className="divide-border flex flex-col divide-y">
          {sites.map((site) => (
            <SiteRow
              key={site.id}
              site={site}
              fallbackName={supplier.name}
              {...(onEdit ? { onEdit: () => onEdit(site) } : {})}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function SiteRow({
  site,
  fallbackName,
  onEdit,
}: {
  readonly site: Site;
  readonly fallbackName: string;
  readonly onEdit?: () => void;
}) {
  const { t } = useTranslation('suppliers');
  const typeLabel = useSiteTypeLabel();
  const Glyph = glyphOf(site.detail ?? site.macro);
  const kind = [
    typeLabel(site.macro, site.detail),
    site.registeredOffice ? t('sites.registeredOffice') : null,
    site.active ? null : t('sites.inactive'),
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
      <span
        aria-hidden="true"
        className="bg-muted text-muted-foreground mt-0.5 grid size-9 shrink-0 place-items-center rounded-lg"
      >
        <Glyph className="size-[18px]" />
      </span>
      <div className="flex min-w-0 grow flex-col">
        <b className="text-sm">{site.sign ?? fallbackName}</b>
        {kind ? <span className="text-muted-foreground text-xs">{kind}</span> : null}
        <AddressLink address={site.address} />
        <PhoneLink phone={site.phone} />
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-muted-foreground text-xs tabular-nums">
          {t('sites.uses', { count: site.uses })}
        </span>
        {onEdit ? (
          <Button
            size="icon"
            variant="ghost"
            className="-mr-2 size-11 @min-[620px]/shell:size-9"
            aria-label={t('sites.edit', { name: site.sign ?? fallbackName })}
            onClick={onEdit}
          >
            <Pencil aria-hidden="true" />
          </Button>
        ) : null}
      </div>
    </li>
  );
}
